import { Command as CommandPrimitive } from 'cmdk';
import { SearchIcon } from 'lucide-react';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils.js';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './dialog.js';

export function Command({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive>): React.ReactNode {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        'flex size-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
        className,
      )}
      {...props}
    />
  );
}

interface CommandDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
  className?: string;
  children?: React.ReactNode;
}

export function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run.',
  children,
  className,
  ...props
}: CommandDialogProps): React.ReactNode {
  return (
    <Dialog {...props}>
      <DialogContent
        showCloseButton={false}
        data-command-dialog
        className={cn(
          'overflow-hidden rounded-lg border-border/50 p-0 shadow-xl max-w-2xl',
          className,
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{description}</DialogDescription>
        <Command className="[&_[cmdk-group-heading]]:px-6 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs max-md:[&_[cmdk-group-heading]]:text-compact [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-input]]:h-12 max-md:[&_[cmdk-input]]:h-14 [&_[cmdk-item]]:px-6 [&_[cmdk-item]]:py-2.5 max-md:[&_[cmdk-item]]:py-3 max-md:[&_[cmdk-item]]:min-h-12 [&_[cmdk-item]]:gap-3">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export function CommandInput({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Input>): React.ReactNode {
  return (
    <div
      data-slot="command-input-wrapper"
      className="flex items-center gap-3 border-b border-border px-6"
    >
      <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          'flex h-11 max-md:h-12 w-full rounded-md bg-transparent py-3 text-sm max-md:text-base outline-none',
          'placeholder:text-muted-foreground',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    </div>
  );
}

export function CommandList({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.List>): React.ReactNode {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        'max-h-[480px] overflow-y-auto overflow-x-hidden overscroll-contain scroll-py-1',
        className,
      )}
      style={{ WebkitOverflowScrolling: 'touch' }}
      {...props}
    />
  );
}

export function CommandEmpty(
  props: ComponentProps<typeof CommandPrimitive.Empty>,
): React.ReactNode {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm text-muted-foreground"
      {...props}
    />
  );
}

export function CommandGroup({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Group>): React.ReactNode {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn('overflow-hidden p-1 text-foreground', className)}
      {...props}
    />
  );
}

export function CommandSeparator({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Separator>): React.ReactNode {
  // cmdk's <Separator> renders with role="separator", which axe (correctly)
  // flags as illegal inside the cmdk-list role="listbox" — listbox children
  // must be options, not separators (WAI-ARIA 1.2 listbox role contract).
  // The separator here is purely visual; render a presentational div with
  // aria-hidden so screen readers skip it and listbox semantics stay clean.
  return (
    <div
      data-slot="command-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
}

export function CommandItem({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Item>): React.ReactNode {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 max-md:gap-3 rounded-md px-[10px] py-2 max-md:py-3 max-md:min-h-11 text-sm max-md:text-base text-foreground outline-none transition-colors',
        'data-[selected=true]:bg-sidebar-accent data-[selected=true]:text-foreground',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
        '[&_svg]:size-4 max-md:[&_svg]:size-5 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

export function CommandShortcut({
  className,
  ...props
}: ComponentProps<'span'>): React.ReactNode {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        'ml-auto text-xs tracking-widest text-foreground/70',
        className,
      )}
      {...props}
    />
  );
}
